import { Head } from '@inertiajs/react'
import AppLayout from '@/layouts/AppLayout'
import { PageProps } from '@/types'
import { DateRange } from '@/components/DateRangeFilter'
import DataTable, { Column } from '@/components/DataTable'
import ReportControlBar, { SelectFilter, applyFilter, makeSortHandler } from '@/components/ReportControlBar'
import { ChartCard, StackedBarChart, ChartColors } from '@/components/ReportCharts'

interface Row {
  referring_agent_id: number
  agent_name: string
  agent_email: string | null
  invites_sent: number
  signed: number
  past_agent: number
  conversion_pct: number
  avg_days_to_sign: number | null
}

interface Props extends PageProps {
  rows: Row[]
  date_range: DateRange
  referring_agents: { id: number; name: string }[]
  filters: { referring_agent_id: number | null }
  sort: { key: string | null; dir: string | null }
}

const PATH = '/admin/reports/recruiter_activity'

export default function RecruiterActivity({ rows, date_range, referring_agents, filters, sort }: Props) {
  const filterState = { referring_agent_id: filters.referring_agent_id ?? '' }
  const onSortChange = makeSortHandler(PATH, date_range, filterState)

  const columns: Column<Row>[] = [
    { key: 'agent_name', header: 'Recruiter', sortable: true,
      render: (r) => (
        <div>
          <div className="font-medium text-chestnut-charcoal">{r.agent_name}</div>
          {r.agent_email && <div className="text-xs text-chestnut-charcoal/60">{r.agent_email}</div>}
        </div>
      )
    },
    { key: 'invites_sent', header: 'Invites', sortable: true, className: 'text-center' },
    { key: 'signed', header: 'Signed', sortable: true, className: 'text-center' },
    { key: 'past_agent', header: 'Past Agent', sortable: true, className: 'text-center' },
    { key: 'conversion_pct', header: 'Conversion %', sortable: true, className: 'text-center',
      render: (r) => `${r.conversion_pct}%` },
    { key: 'avg_days_to_sign', header: 'Avg Days to Sign', sortable: true, className: 'text-center',
      render: (r) => r.avg_days_to_sign == null ? '—' : r.avg_days_to_sign }
  ]

  return (
    <AppLayout>
      <Head title="Recruiter Activity" />
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-chestnut-charcoal font-heading">Recruiter Activity</h1>
          <p className="text-chestnut-charcoal/70 mt-1">Per recruiter: invites sent, signed, conversion %, and average days from invite to signed.</p>
        </div>

        <ReportControlBar path={PATH} dateRange={date_range} filters={filterState} sortState={sort}>
          <SelectFilter
            label="Recruiter"
            value={filters.referring_agent_id ?? ''}
            options={referring_agents.map(a => ({ value: a.id, label: a.name }))}
            onChange={(v) => applyFilter(PATH, { ...filterState, preset: date_range.preset, start_date: date_range.start_date, end_date: date_range.end_date }, 'referring_agent_id', v)}
            allLabel="All recruiters"
          />
        </ReportControlBar>

        <ChartCard
          title="Top 10 recruiters — invites vs. signed"
          subtitle="Stacked bars sum to total invites. Forest = signed; gray = not yet signed. Hover for counts."
          empty={rows.length === 0}
          emptyMessage="No referrals in this date range."
        >
          <StackedBarChart
            data={[...rows]
              .sort((a, b) => b.invites_sent - a.invites_sent)
              .slice(0, 10)
              .map(r => ({
                label: r.agent_name,
                signed: r.signed,
                not_signed: Math.max(r.invites_sent - r.signed, 0)
              }))}
            keys={[
              { key: 'signed', label: 'Signed', color: ChartColors.FOREST },
              { key: 'not_signed', label: 'Not yet signed', color: ChartColors.GRAY }
            ]}
          />
        </ChartCard>

        <div className="bg-white rounded-xl border border-chestnut-gray p-6">
          <DataTable
            data={rows}
            columns={columns}
            searchPlaceholder="Search recruiters..."
            searchKeys={['agent_name', 'agent_email']}
            pageSize={20}
            emptyMessage="No referrals in this date range."
            getRowKey={(r) => r.referring_agent_id}
            controlledSort={{ key: sort.key ?? 'invites_sent', direction: (sort.dir as 'asc' | 'desc') ?? 'desc' }}
            onSortChange={onSortChange}
          />
        </div>
      </div>
    </AppLayout>
  )
}
